import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { assignmentsAPI, curriculaAPI, trainingNodesAPI, quizzesAPI, sopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ClipboardList, 
  Brain,
  FileQuestion,
  BookOpen,
  CheckCircle,
  Clock,
  Calendar,
  Play,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Assignments = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [curricula, setCurricula] = useState({});
  const [trainingNodes, setTrainingNodes] = useState({});
  const [quizzes, setQuizzes] = useState({});
  const [sops, setSOPs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignmentsRes, nodesRes, quizzesRes, sopsRes] = await Promise.all([
        assignmentsAPI.getAll(),
        trainingNodesAPI.getAll(),
        quizzesAPI.getAll(),
        sopsAPI.getAll()
      ]);
      
      setAssignments(assignmentsRes.data);
      
      // Index by ID for quick lookup
      const nodesMap = {};
      nodesRes.data.forEach(n => nodesMap[n.id] = n);
      setTrainingNodes(nodesMap);
      
      const quizzesMap = {};
      quizzesRes.data.forEach(q => quizzesMap[q.id] = q);
      setQuizzes(quizzesMap);
      
      const sopsMap = {};
      sopsRes.data.forEach(s => sopsMap[s.id] = s);
      setSOPs(sopsMap);

      // Load curriculum details for each assignment
      const curriculaMap = {};
      for (const assignment of assignmentsRes.data) {
        if (!curriculaMap[assignment.curriculum_id]) {
          try {
            const currRes = await curriculaAPI.getById(assignment.curriculum_id);
            curriculaMap[assignment.curriculum_id] = currRes.data;
          } catch (e) {
            console.error('Failed to load curriculum:', e);
          }
        }
      }
      setCurricula(curriculaMap);

      if (assignmentsRes.data.length > 0) {
        setSelectedAssignment(assignmentsRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTraining = async (nodeId) => {
    if (!selectedAssignment) return;
    
    try {
      await assignmentsAPI.completeTraining(selectedAssignment.id, nodeId);
      toast.success('Training marked complete');
      loadData();
    } catch (error) {
      toast.error('Failed to mark training complete');
    }
  };

  const handleCompleteSOP = async (sopId) => {
    if (!selectedAssignment) return;
    
    try {
      await assignmentsAPI.completeSOP(selectedAssignment.id, sopId);
      toast.success('SOP marked complete');
      loadData();
    } catch (error) {
      toast.error('Failed to mark SOP complete');
    }
  };

  const getProgress = (assignment) => {
    const curriculum = curricula[assignment.curriculum_id];
    if (!curriculum) return 0;
    
    const totalItems = (curriculum.training_node_ids?.length || 0) + 
                      (curriculum.quiz_ids?.length || 0) + 
                      (curriculum.sop_ids?.length || 0);
    
    if (totalItems === 0) return 0;
    
    const completedItems = (assignment.completed_training_nodes?.length || 0) +
                          (assignment.completed_quizzes?.length || 0) +
                          (assignment.completed_sops?.length || 0);
    
    return Math.round((completedItems / totalItems) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assignments-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-blue-600" />
          My Assignments
        </h1>
        <p className="text-slate-600 mt-1">
          Track your learning progress and complete assigned training
        </p>
      </div>

      {assignments.length === 0 ? (
        <Card className="border-slate-200 shadow-card">
          <CardContent className="text-center py-12">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No assignments yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Your manager will assign training curricula to you
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Assignment List */}
          <div className="space-y-4">
            <h2 className="font-semibold text-slate-900">Your Curricula</h2>
            {assignments.map((assignment) => {
              const curriculum = curricula[assignment.curriculum_id];
              const progress = getProgress(assignment);
              const isSelected = selectedAssignment?.id === assignment.id;
              
              return (
                <Card 
                  key={assignment.id}
                  className={`border-slate-200 shadow-card cursor-pointer transition-all duration-200 
                    ${isSelected ? 'ring-2 ring-blue-500 border-blue-200' : 'hover:border-blue-200'}
                  `}
                  onClick={() => setSelectedAssignment(assignment)}
                  data-testid={`assignment-card-${assignment.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-slate-900">
                          {curriculum?.title || 'Loading...'}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {assignment.status === 'completed' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-200">
                              <Clock className="w-3 h-3 mr-1" />
                              In Progress
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-medium text-slate-900">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <div className="flex items-center gap-1 mt-3 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      Due: {new Date(assignment.due_date).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Assignment Details */}
          <div className="lg:col-span-2">
            {selectedAssignment && curricula[selectedAssignment.curriculum_id] && (
              <Card className="border-slate-200 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading">
                    {curricula[selectedAssignment.curriculum_id]?.title}
                  </CardTitle>
                  {curricula[selectedAssignment.curriculum_id]?.description && (
                    <p className="text-sm text-slate-600">
                      {curricula[selectedAssignment.curriculum_id].description}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="training" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="training" className="flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Training
                      </TabsTrigger>
                      <TabsTrigger value="quizzes" className="flex items-center gap-2">
                        <FileQuestion className="w-4 h-4" />
                        Quizzes
                      </TabsTrigger>
                      <TabsTrigger value="sops" className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        SOPs
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="training" className="mt-4 space-y-3">
                      {curricula[selectedAssignment.curriculum_id]?.training_node_ids?.map((nodeId) => {
                        const node = trainingNodes[nodeId];
                        const isCompleted = selectedAssignment.completed_training_nodes?.includes(nodeId);
                        
                        return (
                          <div 
                            key={nodeId}
                            className={`flex items-center justify-between p-4 rounded-lg border
                              ${isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              {isCompleted ? (
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                  <Brain className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{node?.label || 'Loading...'}</p>
                                <p className="text-xs text-slate-500">{node?.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {node?.link && (
                                <a href={node.link} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm">
                                    <Play className="w-4 h-4 mr-1" />
                                    Watch
                                  </Button>
                                </a>
                              )}
                              {!isCompleted && (
                                <Button 
                                  size="sm"
                                  onClick={() => handleCompleteTraining(nodeId)}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`complete-training-${nodeId}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Complete
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {(!curricula[selectedAssignment.curriculum_id]?.training_node_ids?.length) && (
                        <p className="text-center text-slate-500 py-4">No training modules in this curriculum</p>
                      )}
                    </TabsContent>

                    <TabsContent value="quizzes" className="mt-4 space-y-3">
                      {curricula[selectedAssignment.curriculum_id]?.quiz_ids?.map((quizId) => {
                        const quiz = quizzes[quizId];
                        const isCompleted = selectedAssignment.completed_quizzes?.includes(quizId);
                        
                        return (
                          <div 
                            key={quizId}
                            className={`flex items-center justify-between p-4 rounded-lg border
                              ${isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              {isCompleted ? (
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                  <FileQuestion className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{quiz?.title || 'Loading...'}</p>
                                <p className="text-xs text-slate-500">{quiz?.questions?.length || 0} questions</p>
                              </div>
                            </div>
                            <Link to="/quizzes">
                              <Button variant="outline" size="sm">
                                <Play className="w-4 h-4 mr-1" />
                                {isCompleted ? 'Retake' : 'Start'}
                              </Button>
                            </Link>
                          </div>
                        );
                      })}
                      {(!curricula[selectedAssignment.curriculum_id]?.quiz_ids?.length) && (
                        <p className="text-center text-slate-500 py-4">No quizzes in this curriculum</p>
                      )}
                    </TabsContent>

                    <TabsContent value="sops" className="mt-4 space-y-3">
                      {curricula[selectedAssignment.curriculum_id]?.sop_ids?.map((sopId) => {
                        const sop = sops[sopId];
                        const isCompleted = selectedAssignment.completed_sops?.includes(sopId);
                        
                        return (
                          <div 
                            key={sopId}
                            className={`flex items-center justify-between p-4 rounded-lg border
                              ${isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              {isCompleted ? (
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                  <BookOpen className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{sop?.title || 'Loading...'}</p>
                                <p className="text-xs text-slate-500">{sop?.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Link to="/sops">
                                <Button variant="outline" size="sm">
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Read
                                </Button>
                              </Link>
                              {!isCompleted && (
                                <Button 
                                  size="sm"
                                  onClick={() => handleCompleteSOP(sopId)}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`complete-sop-${sopId}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Complete
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {(!curricula[selectedAssignment.curriculum_id]?.sop_ids?.length) && (
                        <p className="text-center text-slate-500 py-4">No SOPs in this curriculum</p>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Assignments;
